import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import SupplySourceSelector from './SupplySourceSelector';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import Modal from '../common/Modal';

/**
 * Modal de validation marketing avec sélection de source d'approvisionnement
 */
const MarketingValidationModal = ({ requestId, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    comment: '',
    supplySource: null
  });
  const [errors, setErrors] = useState({});
  const [existingSupplySource, setExistingSupplySource] = useState(null);

  // Récupérer la source d'approvisionnement existante pour les demandes Business Unit
  useEffect(() => {
    const fetchExistingSupplySource = async () => {
      try {
        const response = await api.marketingValidation.getRequestSupplySource(requestId);
        console.log('Existing supply source:', response.data);
        
        if (response.data && response.data.data) {
          setExistingSupplySource(response.data.data);
          setFormData({
            ...formData,
            supplySource: {
              category: response.data.data.category,
              source: response.data.data.source
            }
          });
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching supply source:', error);
        setLoading(false);
      }
    };
    
    fetchExistingSupplySource();
  }, [requestId]);

  // Gérer le changement de source d'approvisionnement
  const handleSupplySourceChange = (supplySource) => {
    setFormData({
      ...formData,
      supplySource
    });
    
    // Effacer l'erreur si une source est sélectionnée
    if (supplySource && supplySource.source) {
      setErrors({
        ...errors,
        supplySource: null
      });
    }
  };

  // Gérer le changement de commentaire
  const handleCommentChange = (e) => {
    setFormData({
      ...formData,
      comment: e.target.value
    });
  };

  // Valider le formulaire
  const validateForm = () => {
    const newErrors = {};
    
    // Vérifier que la source d'approvisionnement est sélectionnée
    // Si une source d'approvisionnement existe déjà (cas des demandes Business Unit), on ne vérifie pas
    if (!existingSupplySource && (!formData.supplySource || !formData.supplySource.source)) {
      newErrors.supplySource = 'Veuillez sélectionner une source d\'approvisionnement';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Valider le formulaire
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Appeler l'API pour valider la demande
      await api.marketingValidation.validate(requestId, formData);
      
      toast.success('Demande validée avec succès');
      setLoading(false);
      
      // Notifier le parent du succès
      if (onSuccess) {
        onSuccess();
      }
      
      // Fermer la modal
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error validating request:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la validation de la demande');
      setLoading(false);
    }
  };

  // Distributeur déjà fixé à la création (formulaire animateur) : pas de re-sélection par le marketing
  const prechosenDistributor = existingSupplySource?.distributor?.id ? existingSupplySource.distributor : null;

  return (
    <Modal
      open
      onClose={onClose}
      title="Validation Marketing"
      size="md"
      footer={loading ? null : (
        <>
          <button
            type="button"
            className="modal-btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Annuler
          </button>
          <button
            type="submit"
            form="marketing-validation-form"
            className="modal-btn-success"
            disabled={loading}
          >
            {loading ? 'Validation...' : 'Valider'}
          </button>
        </>
      )}
    >
      {loading ? (
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <form id="marketing-validation-form" onSubmit={handleSubmit}>
          <div>
            <p className="text-sm text-gray-500 mb-4">
                      {prechosenDistributor
                        ? 'Le distributeur a été choisi à la création de la demande. Validez pour transmettre la demande à l\'étape suivante.'
                        : 'Veuillez sélectionner une source d\'approvisionnement pour cette demande, puis validez pour la transmettre à l\'étape suivante.'}
                    </p>
                    
                    {/* Information sur le workflow */}
                    <div className="bg-blue-50 p-3 rounded-md mb-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <InformationCircleIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
                        </div>
                        <div className="ml-3 flex-1 md:flex md:justify-between">
                          <p className="text-sm text-blue-700">
                            <strong>Workflow:</strong> {formData.supplySource?.category === 'internal' ? (
                              'Source interne → Directrice Marketing → Terminé'
                            ) : (
                              'Source distributeur → Force de Vente → Directrice Marketing → Commercial → Terminé'
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Source déjà fixée (distributeur choisi par l'animateur) : lecture seule, sinon sélecteur */}
                    {prechosenDistributor ? (
                      <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm text-gray-800">
                        <span className="font-medium">Distributeur :</span> {prechosenDistributor.name}
                        {prechosenDistributor.code ? ` (${prechosenDistributor.code})` : ''}
                        {existingSupplySource?.selectedBy?.name && (
                          <span className="text-gray-500"> — choisi par {existingSupplySource.selectedBy.name}</span>
                        )}
                      </div>
                    ) : (
                      <SupplySourceSelector
                        onChange={handleSupplySourceChange}
                        value={formData.supplySource}
                        disabled={loading}
                      />
                    )}
                    {errors.supplySource && (
                      <p className="mt-2 text-sm text-red-600">{errors.supplySource}</p>
                    )}
                    
                    {/* Commentaire */}
                    <div className="mt-4">
                      <label htmlFor="comment" className="block text-sm font-medium text-gray-700">
                        Commentaire (optionnel)
                      </label>
                      <textarea
                        id="comment"
                        rows="3"
                        className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border border-gray-300 rounded-md"
                        placeholder="Ajouter un commentaire..."
                        value={formData.comment}
                        onChange={handleCommentChange}
                        disabled={loading}
                      ></textarea>
            </div>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default MarketingValidationModal;
